import {
  Button,
  Col,
  ConfigProvider,
  Image,
  Input,
  Layout,
  Row,
  Spin,
  message,
} from "antd";
import { Content } from "antd/es/layout/layout";
import React, { useContext, useEffect, useState } from "react";
import { useAuth } from "../contexts/Auth";
import { useBrandContext } from "../contexts/Brand";
import { useNavigate } from "react-router-dom";
import styles from "../styles/LoginForm.module.css";
import useWindowDimensions from "../hooks/useWindowDimensions";
import { screenWidths } from "../const/const_functions";
import { useServer } from "../contexts/Server";
import LoginSubText from "../components/LoginSubText";
import LoginTitle from "../components/LoginTitle";

const LOGIN_STEPS = {
  LOGIN: 1,
  FORGOT_PASSWORD: 2,
  VERIFY_CODE: 3,
};

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [tempEmail, setTempEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationCodeError, setVerificationCodeError] = useState(false);
  const [loginStep, setLoginStep] = useState(LOGIN_STEPS.LOGIN);
  const { loginWithUsernameAndPassword, authData, loadingAuth } = useAuth();
  const { brandContextData } = useBrandContext();
  const navigate = useNavigate();
  const { serverData } = useServer();
  const { theme } = useContext(ConfigProvider.ConfigContext);

  useEffect(() => {
    if (authData) {
      if (brandContextData?.brandType === "Invoice") {
        navigate("/edit-account");
      } else {
        navigate("/");
      }
    }
  }, [authData]);

  const validateEmail = (email) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const validatePassword = (password) => {
    return typeof password === "string" && password.length >= 6;
  };

  const loginUser = async () => {
    if (!tempEmail) {
      message.error("Please input an email");
      return;
    }

    if (!password) {
      message.error("Please input a password");
      return;
    }

    try {
      await loginWithUsernameAndPassword({
        email: tempEmail,
        username: `${tempEmail}++||++${brandContextData._id}`,
        password,
      });

      if (brandContextData?.brandType === "Invoice") {
        navigate("/edit-account");
      } else {
        navigate("/");
      }
    } catch (e) {
      console.log(e);
    }
  };

  let mobile = false;
  const { width } = useWindowDimensions();
  if (screenWidths.md > width) {
    mobile = true;
  }

  const sendVerificationCode = async () => {
    const email = tempEmail.toLowerCase();
    const requestFrom = "whitelabel-web";
    const brandContextId = brandContextData._id

    setSendingCode(true);
    try {
      await serverData.call("USSR_requestPasswordResetCode", { email, requestFrom, brandContextId });
      message.success(
        "Please check your email for the security code needed to reset your password",
        5
      );
      setSendingCode(false);
      setLoginStep(LOGIN_STEPS.LOGIN);
    } catch (e) {
      message.error(
        "The email address you entered is incorrect or does not exist!",
        5
      );
      setSendingCode(false);
    }
  };

  const resetPassword = async (passedCode, passedPassword) => {
    if (passwordError) {
      return;
    }
    const code = passedCode;
    const passwordSubmit = passedPassword;
    const email = tempEmail.toLowerCase();
    const brandContextId = brandContextData._id;
    try {
      await serverData.call("USSR_submitPasswordResetCode", {
        code,
        email,
        brandContextId,
        password: passwordSubmit,
      });
      message.success("Password reset successfully! Please login.", 5);
      setVerificationCodeError(false);
      setPasswordError(false);
      setLoginStep(LOGIN_STEPS.LOGIN);
    } catch (e) {
      message.error("Security code has expired", 5);
      setVerificationCodeError(true);
    }
  };

  let pageTitle = `Login`;
  let pageSubtitle = `Welcome to ${brandContextData.brandName}.`;
  let enterButtonText = `Login`;
  let passwordText = `Password`;
  const emailErrorText = `Please input a valid email`;
  const verificationCodeErrorText = `Invalid code`;
  const passwordErrorText = `Please input a password at least 6 characters long`;
  if (loginStep === LOGIN_STEPS.FORGOT_PASSWORD) {
    pageTitle = `Forgot Password`;
    pageSubtitle = `Please enter your email and we'll send you a link to reset your password.`;
    enterButtonText = `Send Reset Link`;
  } else if (loginStep === LOGIN_STEPS.VERIFY_CODE) {
    pageTitle = `Reset Password`;
    pageSubtitle =
      "Email sent! Please follow the link sent to your email to verify your account.";
    enterButtonText = `Confirm`;
    passwordText = `New Password`;
  }

  const resendVerification = async () => {
    try {
      const resendVerificationRes = await serverData.call(
        "USSR_resendVerificationEmail",
        { contextId: brandContextData._id }
      );
      if (resendVerificationRes?.success) {
        alert("An email with a link to verify your email has been sent to you");
      }
    } catch (e) {
      alert(
        "There was an error, please contact support if the issue persists."
      );
    }
  };

  useEffect(() => {
    if (loginStep === LOGIN_STEPS.LOGIN) {
      setTempEmail("");
      setEmailError(false);
    }
    setPassword("");
    setPasswordError(false);
    setVerificationCode("");
    setVerificationCodeError(false);
  }, [loginStep]);

  if (loadingAuth || authData) {
    return null;
  }

  const handleUserAuthentication = () => {
    if (loginStep === LOGIN_STEPS.LOGIN) {
      loginUser();
    } else if (loginStep === LOGIN_STEPS.FORGOT_PASSWORD) {
      sendVerificationCode();
    } else if (loginStep === LOGIN_STEPS.VERIFY_CODE) {
      resetPassword(verificationCode, password);
    }
  };
  return (
    <Layout
      style={{
        backgroundColor: "rgb(237, 237, 237)",
        minHeight: "100vh",
      }}
    >
      <Spin
        size="large"
        spinning={loading || sendingCode}
        tip={loading ? "Logging In." : "Sending Code..."}
      >
        <Content
          style={{
            marginTop: 20,
            marginBottom: 100,
            flexDirection: "column",
            display: "flex",
          }}
        >
          <Col
            style={{ backgroundColor: "white", padding: 48, borderRadius: 12 }}
            xs={{ span: 22, offset: 1 }}
            md={{ span: 8, offset: 8 }}
          >
            {brandContextData?.brandLogo ? (
              <Row justify={"center"} className={styles.logoContainer}>
                <Image
                  width={mobile ? "50%" : 200}
                  preview={false}
                  src={brandContextData.brandLogo}
                  style={{
                    maxHeight: 180,
                    minHeight: 75,
                    objectFit: "contain",
                  }}
                />
              </Row>
            ) : null}
            <p
              style={{
                fontWeight: "600",
                color: "black",
                fontSize: 24,
                marginBottom: 0,
              }}
            >
              {pageTitle}
            </p>
            <p
              style={{
                fontWeight: "500",
                color: "grey",
                marginBottom: 40,
                fontSize: 14,
                marginTop: 4,
              }}
            >
              {pageSubtitle}
            </p>
            {loginStep === LOGIN_STEPS.LOGIN ||
              loginStep === LOGIN_STEPS.FORGOT_PASSWORD ? (
              <LoginTitle titleText={`E-mail`} required={true} />
            ) : null}
            {loginStep === LOGIN_STEPS.LOGIN ||
              loginStep === LOGIN_STEPS.FORGOT_PASSWORD ? (
              <Input
                className={styles.input}
                style={{ "--border-color": theme.token.colorSecondary }}
                value={tempEmail}
                defaultValue={null}
                onChange={(e) => {
                  if (
                    (e.target.value && validateEmail(e.target.value)) ||
                    !e.target.value
                  ) {
                    setTempEmail(e.target.value);
                    setEmailError(false);
                  } else {
                    setTempEmail(e.target.value);
                    setEmailError(true);
                  }
                }}
              />
            ) : null}
            {loginStep === LOGIN_STEPS.LOGIN ||
              loginStep === LOGIN_STEPS.FORGOT_PASSWORD ? (
              <LoginSubText showError={emailError} errorText={emailErrorText} />
            ) : null}
            {/* {loginStep === LOGIN_STEPS.VERIFY_CODE ? (
              <LoginTitle titleText={`Verification Code`} required={true} />
            ) : null} */}
            {/* {loginStep === LOGIN_STEPS.VERIFY_CODE ? (
              <Input
                className={styles.input}
                style={{ "--border-color": theme.token.colorSecondary }}
                value={verificationCode}
                defaultValue={null}
                onChange={(e) => {
                  setVerificationCode(e.target.value);
                }}
                maxLength={10}
              />
            ) : null} */}
            {loginStep === LOGIN_STEPS.VERIFY_CODE ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <p>Didn't receive an email?</p>
                <Button>Re-send Email</Button>
              </div>
            ) : null}
            {loginStep === LOGIN_STEPS.LOGIN ? (
              <LoginTitle titleText={passwordText} required={true} />
            ) : null}
            {loginStep === LOGIN_STEPS.LOGIN ? (
              <Input
                className={styles.input}
                style={{ "--border-color": theme.token.colorSecondary }}
                value={password}
                defaultValue={null}
                type="password"
                onChange={(e) => {
                  if (
                    (e.target.value && validatePassword(e.target.value)) ||
                    !e.target.value
                  ) {
                    setPassword(e.target.value);
                    setPasswordError(false);
                  } else {
                    setPassword(e.target.value);
                    setPasswordError(true);
                  }
                }}
              />
            ) : null}
            {loginStep === LOGIN_STEPS.LOGIN ||
              loginStep === LOGIN_STEPS.VERIFY_CODE ? (
              <LoginSubText
                showError={passwordError}
                errorText={passwordErrorText}
                showSubText={loginStep === LOGIN_STEPS.LOGIN}
                subText={"Forgot Password?"}
                onClickAction={() => setLoginStep(LOGIN_STEPS.FORGOT_PASSWORD)}
              />
            ) : null}
            <div
              style={{
                display: "flex",
                justifyContent:
                  loginStep !== LOGIN_STEPS.LOGIN
                    ? "space-between"
                    : "flex-end",
                marginTop: 22,
              }}
            >
              {loginStep !== LOGIN_STEPS.LOGIN ? (
                <Button
                  className={styles.loginBtn}
                  style={{
                    "--border-color": theme.token.colorSecondary,
                    width: "100%",
                  }}
                  onClick={() => setLoginStep(LOGIN_STEPS.LOGIN)}
                >
                  Back to login
                </Button>
              ) : null}
              {loginStep !== LOGIN_STEPS.VERIFY_CODE ? (
                <Button
                  className={styles.loginBtn}
                  style={{
                    "--border-color": theme.token.colorSecondary,
                    width: "100%",
                    backgroundColor: theme.token.colorSecondary,
                  }}
                  onClick={() => {
                    if (brandContextData.enableAccounts) {
                      handleUserAuthentication();
                    } else {
                      navigate("/");
                    }
                  }}
                >
                  <span style={{ color: "white", fontWeight: "600" }}>
                    {enterButtonText}
                  </span>
                </Button>
              ) : null}
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <p>
                Don't have an account?{" "}
                <a
                  onClick={() => navigate("/create-account")}
                  style={{
                    color: theme.token.colorSecondary,
                    fontWeight: "600",
                  }}
                >
                  Create One
                </a>
              </p>
            </div>
          </Col>
        </Content>
      </Spin>
    </Layout>
  );
};

export default Login;
